/**
 * Lifetime Product Adapters
 *
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 *
 * Adapters for one-time/lifetime purchase data transformation.
 *
 * FCR S291 Issue 8 (DD-3): all Stripe field reads use the dual-path helpers
 * in ./_stripe-helpers.ts so the adapter works against both legacy
 * (≤2025-12) and API 2026-04-22.dahlia shapes. Direct ``item.price.product``
 * / ``invoice.payment_intent`` reads return null under v15 and would render
 * every lifetime product card as "No license key" / "No Devices".
 */

import {
  filterLicensesByProduct,
} from "@/lib/license-matching";
import { License, Order } from "@/types";
import {
  fetchLicense,
  fetchProductName,
} from "@/utils/dashboardUtils";
import { getProductDisplayName } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { DEPRECATED_LIFETIME_PRODUCT_IDS } from "@/lib/constants";
import type { LifetimeProduct } from "./adapter-types";
import {
  getInvoicePaymentIntent,
  getLineProductId,
  getLinePriceId,
  getLineType,
} from "./_stripe-helpers";
import { isDocumentTranslator } from "@/lib/document-translator-pricing";

export const adaptLifetimeProducts = async (
  invoices: Order[],
): Promise<LifetimeProduct[]> => {
  if (!invoices || invoices.length === 0) {
    logger.info("No invoices found - returning empty lifetime products");
    return [];
  }

  const lifetimeLineItems = invoices.flatMap((invoice) => {
    if (!invoice?.lines?.data || !Array.isArray(invoice.lines.data)) {
      return [];
    }

    return invoice.lines.data
      // FCR S291 Issue 8: getLineType reads price.type (legacy) or
      // parent.type (v15). Direct ``item.price.type`` returns undefined
      // under v15 because price moved to pricing.price_details.
      .filter((item) => getLineType(item) === "one_time")
      // Guardrail 2: Exclude deprecated one-time products (historical TSL
      // Extra Words purchases). These appear in old invoices but are no
      // longer sold; including them creates a dashboard entry with an empty
      // license and risks a TypeError crash in use-newUser when licenses[0]
      // is accessed.
      .filter(
        (item) =>
          !DEPRECATED_LIFETIME_PRODUCT_IDS.has(getLineProductId(item) as string),
      )
      // FCR S291 Issue 8: getInvoicePaymentIntent walks the v15 nested
      // payments.data[0].payment.payment_intent path when the legacy
      // top-level field is null.
      .map((item) => ({
        ...item,
        payment_intent: getInvoicePaymentIntent(invoice),
      }));
  });

  if (lifetimeLineItems.length === 0) {
    logger.info("No lifetime line items found in invoices");
    return [];
  }

  // NF-3: Group by productId:priceId to keep Annual and Lifetime variants separate.
  // Grouping by productId only merges them on the dashboard after SK-U migration.
  const groups = new Map<string, typeof lifetimeLineItems>();
  for (const item of lifetimeLineItems) {
    // FCR S291 Issue 8: dual-path productId / priceId resolution.
    const productId = getLineProductId(item);
    if (!productId) {
      continue;
    }
    const priceId = getLinePriceId(item) || "";
    // S-4: DT uses inline price_data so each purchase above 100 words gets a unique
    // anonymous price ID. Group by productId only so all DT purchases share one card.
    const groupKey = isDocumentTranslator(productId)
      ? productId
      : (priceId ? `${productId}:${priceId}` : productId);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(item);
  }

  const productPromises = Array.from(groups.entries()).map(
    async ([_groupKey, group]) => {
      // S295/DD-1: pull productId from the same getter the group key was built
      // from. The earlier `if (!productId) continue` guard above ensures every
      // grouped item has a string productId, so `getLineProductId(group[0])`
      // returns string here too — no `as string` cast needed.
      const productId = getLineProductId(group[0]);
      if (!productId) {
        // Defensive: cannot happen given the grouping loop above, but typing
        // this as `string | undefined` requires a runtime check before use.
        return null;
      }

      const licensesNested: License[][] = await Promise.all(
        group.map(async (item) => {
          // S295/BE-2: `payment_intent` is `string | null` since helpers were
          // extracted (some Stripe transitional invoices on the new API —
          // refunded, zero-amount subscription — return a null
          // payment_intent). `fetchLicense` requires a string, so the call
          // would type-error without this guard, and the licenses scoped to
          // a null payment intent cannot be retrieved anyway.
          if (!item.payment_intent) {
            logger.warn(
              `No payment intent for product ${productId} - skipping license fetch`,
            );
            return [];
          }
          try {
            const licenses = await fetchLicense(item.payment_intent, productId);
            return Array.isArray(licenses) ? licenses : [];
          } catch (error) {
            logger.error(
              `Failed to fetch license for payment intent ${item.payment_intent}:`,
              error,
            );
            return [];
          }
        }),
      );

      // Build per-license quantity map before flattening.
      // group[i] is the purchase that generated licensesNested[i], so we can
      // associate each ActivationKey with its exact Stripe quantity.
      // Without this, group[0].quantity is applied to every license regardless
      // of which purchase created it, producing inflated device counts when a
      // user has made multiple purchases with different quantities.
      const licenseQuantityMap = new Map<string, number>();
      licensesNested.forEach((licensesForPurchase, i) => {
        const qty = group[i].quantity ?? 1;
        licensesForPurchase.forEach((l) => {
          if (l.ActivationKey) licenseQuantityMap.set(l.ActivationKey, qty);
        });
      });

      const flatLicenses = licensesNested.flat();
      // Pass the number of distinct licenses as a proxy for products-in-order.
      // In QLM, each product purchase produces exactly one license key regardless
      // of quantity; quantity only affects NumLicenses. So flatLicenses.length > 1
      // reliably indicates a multi-product payment intent — enabling the
      // cross-product contamination guard inside filterLicensesByProduct.
      const totalProductsInOrder = Math.max(flatLicenses.length, 1);
      const matchResult = filterLicensesByProduct(flatLicenses, productId, totalProductsInOrder);
      const licenses = matchResult.licenses;

      // If no matching licenses found from payment-intent-scoped fetch, show 0
      // rather than widening the search to ALL user licenses via fetchLicenses().
      // The wider search caused cross-order contamination — licenses from
      // completely unrelated past orders could appear under the wrong product.
      if (licenses.length === 0 && flatLicenses.length > 0) {
        logger.warn(
          `[Lifetime ${productId}] No matching licenses from payment intent ` +
            `(${flatLicenses.length} fetched, 0 matched). ` +
            `Check UserData1 population in QLM.`,
        );
      }

      if (matchResult.legacyMatchCount > 0) {
        logger.info(
          `[Lifetime ${productId}] ${matchResult.legacyMatchCount} legacy license(s) matched by ProductID`,
        );
      }

      // S256 (FCR §3): lifetime products are always `type: "one_time"` by
      // definition — this branch only runs after `.filter(item => item?.price?.type === "one_time")`.
      // Route the name through getProductDisplayName so every dashboard
      // surface shows the same "- Lifetime License" suffix.
      const baseProductName = await fetchProductName(productId);
      const productName = getProductDisplayName(
        baseProductName,
        "one_time",
        undefined,
        productId,
      );

      let totalDevices = 0;
      let availableDevices = 0;
      licenses.forEach((license) => {
        // F-9 (S343): QLM's MaxActivations is authoritative — it already includes
        // the +1 applied at license creation (num_seats = quantity + 1).
        // Using raw Stripe quantity here loses that +1 and produces the wrong total.
        const stripeQuantity = licenseQuantityMap.get(license.ActivationKey) ?? 1;
        const maxActivations =
          license.MaxActivations ??
          license.NumLicenses ??
          (stripeQuantity > 1 ? stripeQuantity + 1 : 1);
        totalDevices += maxActivations;
        // F-9 (S343): Clamp to prevent negative available count when QLM temporarily
        // reports AvailableLicenses > NumLicenses right after license creation.
        availableDevices += Math.max(0,
          license.AvailableLicenses ?? Math.max(0, maxActivations - (license.ActivationCount ?? 0)));
      });

      const totalLicensesCount = licenses.reduce(
        (sum, license) => {
          const stripeQuantity = licenseQuantityMap.get(license.ActivationKey) ?? 1;
          // F-9 (S343): Prefer QLM's NumLicenses (already includes +1);
          // fall back to Stripe qty + 1 only when QLM data is absent.
          return sum + (license.NumLicenses ?? (stripeQuantity > 1 ? stripeQuantity + 1 : 1));
        },
        0,
      );

      return {
        id:
          group[0].payment_intent
          || (group[0] as { id?: string }).id
          || `${productId}:${getLinePriceId(group[0]) || ""}`,
        productId: productId,
        productName: productName,
        licenses: licenses,
        totalLicenses: totalLicensesCount,
        totalDevices: totalDevices,
        availableDevices: availableDevices,
        // A-4: period.end is the Unix timestamp of the invoice line item period end.
        // Newest-first sort on My Products page. group[0] is the most recent item.
        createdAt: (group[0] as { period?: { end?: number } }).period?.end ?? 0,
      };
    },
  );

  // S295/DD-1: filter out the defensive `null` returns from the productId guard
  // above so the public return type `LifetimeProduct[]` holds.
  const results = await Promise.all(productPromises);
  const activeLifetime = results.filter((r) => r !== null) as LifetimeProduct[];

  // CHANGE_06 (FCR S483 Bugs 2-3): Build refunded entries from invoices with
  // refund_state="full". These invoices were excluded from the main group
  // processing above (their licenses are revoked). Surface them with a status
  // label and "Buy again" link so customers see their purchase history.
  //
  // The refund_state field is enriched server-side and not on the Order type;
  // use a cast. isDocumentTranslator entries are skip (DT uses a separate tab).
  const refundedEntries: LifetimeProduct[] = [];
  for (const invoice of invoices) {
    const refundState = (invoice as { refund_state?: string }).refund_state;
    if (refundState !== "full") continue;

    const lineItems = invoice?.lines?.data || [];
    for (const item of lineItems) {
      if (getLineType(item) !== "one_time") continue;
      const productId = getLineProductId(item);
      if (!productId) continue;
      if (isDocumentTranslator(productId)) continue;
      if (DEPRECATED_LIFETIME_PRODUCT_IDS.has(productId)) continue;

      // Skip if an active entry already covers this product (e.g. repurchase).
      if (activeLifetime.some((r) => r.productId === productId)) continue;

      const productName = await fetchProductName(productId);
      const invCreated = (invoice as { created?: number }).created;
      const accessEndDate = invCreated
        ? new Date(invCreated * 1000).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : undefined;

      refundedEntries.push({
        id: `refunded:${invoice.id}:${productId}`,
        productId,
        productName: productName || "Deskshare Software",
        licenses: [],
        totalLicenses: 0,
        totalDevices: 0,
        availableDevices: 0,
        createdAt: invCreated ?? 0,
        // Cast to carry displayStatus through to Product in product-adapter
        ...(({ displayStatus: "refunded", accessEndDate } as unknown) as object),
      });

      break; // one entry per invoice
    }
  }

  return [...activeLifetime, ...refundedEntries];
};
